import React, { useState, useEffect, useRef } from "react";
import Icon from "./common/Icon";
import "./LevelAccordion.css";

// Levels 1..count. loadLevel(level) -> Promise<string[]> (member ids), cached.
// When preloadCounts is true, all levels are loaded on mount so each row shows
// its member count up front. `resetKey` clears the cache (e.g. package change).
export default function LevelAccordion({ count = 10, loadLevel, resetKey, preloadCounts = true }) {
  const [open, setOpen] = useState(null);
  const [cache, setCache] = useState({});   // level -> ids[]
  const [loading, setLoading] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Reset + (optionally) preload counts whenever the context changes.
  useEffect(() => {
    let cancelled = false;
    setOpen(null);
    setCache({});
    setLoading(null);

    if (preloadCounts) {
      (async () => {
        for (let L = 1; L <= count; L++) {
          try {
            const ids = await loadLevel(L);
            if (cancelled || !mounted.current) return;
            setCache((c) => ({ ...c, [L]: ids || [] }));
          } catch {
            if (cancelled || !mounted.current) return;
            setCache((c) => ({ ...c, [L]: [] }));
          }
        }
      })();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [resetKey]);

  const toggle = async (level) => {
    if (open === level) { setOpen(null); return; }
    setOpen(level);
    if (cache[level] === undefined) {
      setLoading(level);
      try {
        const ids = await loadLevel(level);
        if (mounted.current) setCache((c) => ({ ...c, [level]: ids || [] }));
      } catch {
        if (mounted.current) setCache((c) => ({ ...c, [level]: [] }));
      } finally {
        if (mounted.current) setLoading(null);
      }
    }
  };

  return (
    <div className="lvl-list">
      {Array.from({ length: count }, (_, i) => i + 1).map((level) => {
        const isOpen = open === level;
        const ids = cache[level];
        const known = ids !== undefined;
        const isLoading = loading === level;
        return (
          <div className={`lvl-item ${isOpen ? "open" : ""}`} key={level}>
            <button className="lvl-head" onClick={() => toggle(level)}>
              <span className="lvl-badge">L{level}</span>
              <span className="lvl-title">Level {level}</span>
              <span className={`lvl-count ${known && ids.length > 0 ? "has" : ""}`}>
                <Icon name="users" size={13} />
                {known ? ids.length : <span className="lvl-dot" />}
              </span>
              <span className={`lvl-chev ${isOpen ? "up" : ""}`}>▾</span>
            </button>
            {isOpen && (
              <div className="lvl-body">
                {isLoading && <span className="muted">Loading…</span>}
                {!isLoading && ids && ids.length === 0 && (
                  <span className="muted">No members at this level yet.</span>
                )}
                {!isLoading && ids && ids.length > 0 && (
                  <div className="row wrap">
                    {ids.map((id, k) => (
                      <span className="id-chip" key={k}>{id}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
